import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Réseaux Fibre Optique — IS TELECOM',
  description: 'Déploiement de réseaux fibre optique FTTH, backbones longue distance, épissurage fusion, tests OTDR. Partenaires des grands opérateurs africains.',
  provider: {
    '@type': 'Organization',
    name: 'IS TELECOM',
    url: 'https://is-telecom.co',
  },
  areaServed: 'Afrique, Asie, Europe, Amériques',
};

export default function TelecomFibre() {
  const { t } = useTranslation();

  const services = [
    {
      title: 'FTTH — Fiber To The Home',
      desc: 'Déploiement complet de réseaux fibre jusqu\'au domicile : études, tirage de câbles, pose de boîtiers, raccordement abonnés. Technologies GPON et XGS-PON pour des débits jusqu\'à 10 Gbps.',
      icon: 'ri-home-wifi-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20fiber%20optic%20cable%20installation%20team%20in%20residential%20neighborhood%2C%20FTTH%20deployment%20with%20dark-skinned%20optical%20fiber%20technicians%20pulling%20cables%20through%20African%20urban%20streets%2C%20modern%20telecommunications%20infrastructure%2C%20professional%20Black%20engineer%20working%20with%20fiber%20equipment%2C%20bright%20daylight%20photography&width=800&height=500&seq=tel-ftth-01&orientation=landscape&nocache=true',
    },
    {
      title: 'Backbones & Longue Distance',
      desc: 'Conception et déploiement de backbones fibre interurbains et internationaux. Câbles enterés, aériens et sous-marins. Capacités allant de quelques fibres à plusieurs milliers.',
      icon: 'ri-route-line',
      image: 'https://readdy.ai/api/search-image?query=Aerial%20fiber%20optic%20cable%20backbone%20across%20African%20landscape%2C%20long%20distance%20telecommunications%20lines%20on%20utility%20poles%20stretching%20across%20rural%20terrain%2C%20professional%20telecom%20infrastructure%20photography%20with%20dramatic%20sky%2C%20warm%20golden%20hour%20lighting&width=800&height=500&seq=tel-backbone-01&orientation=landscape',
    },
    {
      title: 'Épissurage & Tests OTDR',
      desc: 'Épissurage par fusion avec pertes inférieures à 0.05 dB. Tests de transmission OTDR complets, validation des réflectométries et certification de chaque lien déployé.',
      icon: 'ri-tools-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20fiber%20optic%20splicing%20technician%20with%20dark%20skin%20using%20fusion%20splicer%20machine%2C%20close-up%20of%20Black%20engineer%20performing%20optical%20fiber%20jointing%20process%20with%20precision%20equipment%2C%20professional%20telecom%20network%20testing%20by%20African%20technician%2C%20clean%20technical%20workspace%20with%20focused%20lighting&width=800&height=500&seq=tel-splicing-01&orientation=landscape&nocache=true',
    },
    {
      title: 'Data Centers & Hubs',
      desc: 'Câblage structuré fibre pour data centers, hubs de télécommunication et points de présence. Connecteurs MPO/MTP, gestion de câbles et documentation complète des réseaux.',
      icon: 'ri-server-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20data%20center%20with%20fiber%20optic%20cable%20management%20and%20network%20racks%2C%20organized%20cable%20trays%20with%20colored%20fiber%20optic%20cables%2C%20professional%20telecommunications%20facility%20with%20clean%20industrial%20aesthetic%2C%20bright%20artificial%20lighting%2C%20high-tech%20infrastructure&width=800&height=500&seq=tel-dc-01&orientation=landscape',
    },
  ];

  const stats = [
    { value: '15000', suffix: '+', label: 'km de fibre déployés' },
    { value: '200', suffix: '+', label: 'villes connectées' },
    { value: '0.05', suffix: 'dB', label: 'Perte par épissure' },
    { value: '10', suffix: 'Gbps', label: 'Débit XGS-PON' },
  ];

  const techs = [
    { name: 'GPON', desc: 'Gigabit Passive Optical Network — déploiement massif à coût réduit.' },
    { name: 'XGS-PON', desc: '10 Gbps symétrique — nouvelle génération pour zones denses.' },
    { name: 'DWDM', desc: 'Dense Wavelength Division Multiplexing — backbone haute capacité.' },
    { name: 'OTDR', desc: 'Optical Time Domain Reflectometer — tests de qualité certifiés.' },
  ];

  return (
    <Layout>
      <JsonLd data={schema} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-background-950">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20glowing%20fiber%20optic%20network%20visualization%20with%20light%20beams%20and%20data%20streams%2C%20dark%20background%20with%20blue%20and%20green%20luminous%20fiber%20cables%20creating%20mesh%20pattern%2C%20futuristic%20telecommunications%20concept%20art%2C%20professional%20digital%20illustration%20for%20telecom%20company&width=1920&height=900&seq=tel-fibre-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-accent-400/50 rounded-full px-5 py-2.5 mb-8">
            <i className="ri-route-line text-accent-400 text-sm w-4 h-4 flex items-center justify-center" />
            <span className="text-accent-300 text-sm font-medium uppercase tracking-wider">Télécommunications</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-white mb-6 leading-[1.1]">
            Réseaux<br />
            <span className="text-accent-400">Fibre Optique</span>
          </h1>
          <p className="text-lg md:text-xl text-background-200 max-w-2xl mx-auto leading-relaxed mb-10">
            FTTH, backbones longue distance, épissurage et tests OTDR.
            Nous connectons les villes, les pays et les continents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold font-heading text-accent-400 mb-1">
                  {s.value}<span className="text-2xl">{s.suffix}</span>
                </div>
                <div className="text-background-300 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <ScrollReveal variant="fade-up">
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Nos Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                De l&apos;étude à la certification
              </h2>
              <p className="text-foreground-500 mt-4 max-w-2xl mx-auto">
                Chaque kilomètre de fibre est épissuré, testé et certifié. Notre process garantit la fiabilité de vos réseaux sur le long terme.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <div key={svc.title} className="group bg-white rounded-3xl overflow-hidden border border-background-200/70 hover:shadow-xl transition-all duration-500">
                  <div className="h-56 overflow-hidden">
                    <img src={svc.image} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <i className={`${svc.icon} text-primary-600 text-xl w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <h3 className="text-xl font-bold font-heading text-foreground-950">{svc.title}</h3>
                    </div>
                    <p className="text-foreground-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Technologies */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Technologies</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                Les technologies de demain
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {techs.map((t) => (
                <div key={t.name} className="bg-white rounded-3xl p-6 border border-background-200/70 hover:shadow-md transition-shadow text-center">
                  <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-wifi-line text-primary-600 text-2xl w-7 h-7 flex items-center justify-center" />
                  </div>
                  <h3 className="font-bold font-heading text-foreground-950 mb-2">{t.name}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">
            Connectez votre territoire à la fibre
          </h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            Que ce soit pour un réseau métropolitain, un backbone national ou un déploiement FTTH, nous avons l&apos;expertise et les équipes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-primary-400 text-primary-400 hover:bg-primary-500/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}